import { useCallback, useState } from 'react';
import { useData, useAuth, useNotifs } from '../contexts/index';
import {
  canDeleteUser,
  canManageUser,
  type UserManagementAction,
} from '../core/rules/userManagement';
import { buildRegisterStaffDto, buildStaffProfilePatch } from '../core/dtos/index';
import { useMessaging } from './useMessaging';
import { usePresentAppError } from './usePresentAppError';

export type UserPanelSheet =
  | { type: 'register'; houseId: string }
  | { type: 'edit'; user: Record<string, unknown>; houseId: string }
  | { type: 'message'; user: Record<string, unknown>; houseId: string }
  | { type: 'payment'; user: Record<string, unknown>; houseId: string }
  | { type: 'agreements'; user: Record<string, unknown>; houseId: string }
  | { type: 'devices'; user: Record<string, unknown>; houseId: string }
  | { type: 'delete'; user: Record<string, unknown>; houseId: string }
  | null;

export function useUserManagementPanel(t: Record<string, string>) {
  const { adapter, messagingAdapter } = useData();
  const { user: viewer } = useAuth();
  const { push } = useNotifs();
  const presentAppError = usePresentAppError();
  const { sendDirectMessage } = useMessaging();
  const [sheet, setSheet] = useState<UserPanelSheet>(null);

  const closeSheet = useCallback(() => setSheet(null), []);

  const toastError = useCallback((err: unknown, feature: string) => {
    presentAppError(err, { t, push, context: { feature } });
  }, [presentAppError, t, push]);

  const handleAction = useCallback((
    action: UserManagementAction,
    target: Record<string, unknown>,
    houseId: string,
  ) => {
    if (action === 'suspend') {
      if (!canManageUser(viewer, target, houseId || undefined)) return;
      void adapter.toggleSanction(String(target.id)).then(() => {
        push({
          cat: 'users',
          sev: target.status === 'suspended' ? 'success' : 'warn',
          title: target.status === 'suspended' ? t.userSanctionLifted : t.userSuspended,
          body: String(target.name ?? ''),
        });
      }).catch((err) => toastError(err, 'userSuspend'));
      return;
    }
    if (action === 'delete') {
      setSheet({ type: 'delete', user: target, houseId });
      return;
    }
    if (action === 'edit') {
      setSheet({ type: 'edit', user: target, houseId });
      return;
    }
    if (action === 'message') {
      setSheet({ type: 'message', user: target, houseId });
      return;
    }
    if (action === 'payment') {
      setSheet({ type: 'payment', user: target, houseId });
      return;
    }
    if (action === 'agreements') {
      setSheet({ type: 'agreements', user: target, houseId });
      return;
    }
    if (action === 'devices') {
      setSheet({ type: 'devices', user: target, houseId });
    }
  }, [adapter, viewer, push, t, toastError]);

  const registerUser = useCallback(async (form: Record<string, unknown>) => {
    if (!adapter.registerStaffUser) throw new Error('register-unavailable');
    try {
      const dto = buildRegisterStaffDto(form);
      const data = await adapter.registerStaffUser(dto);
      const uid = String(data.id ?? '');
      const houseId = String(dto.houseId || form.houseId || '');
      const profilePhoto = form.profilePhoto instanceof File ? form.profilePhoto : null;
      const ciFront = form.ciFront instanceof File ? form.ciFront : null;
      const ciBack = form.ciBack instanceof File ? form.ciBack : null;
      if (
        uid
        && houseId
        && adapter.name === 'firebase'
        && (profilePhoto || ciFront || ciBack)
        && adapter.updateStaffUser
      ) {
        try {
          const { uploadStaffRegistrationMedia } = await import(
            '../services/firebase/staffMediaUpload'
          );
          const media = await uploadStaffRegistrationMedia({
            houseId,
            uid,
            profilePhoto,
            ciFront,
            ciBack,
          });
          if (Object.keys(media).length > 0) {
            await adapter.updateStaffUser(uid, media);
          }
        } catch (mediaErr) {
          console.warn('[registerUser] media upload failed', mediaErr);
          push({
            cat: 'users',
            sev: 'warn',
            title: t.userRegistered,
            body: t.registerMediaUploadFailed || '',
          });
        }
      }
      push({ cat: 'users', sev: 'success', title: t.userRegistered, body: dto.name });
      return data;
    } catch (err) {
      toastError(err, 'userRegister');
      throw err;
    }
  }, [adapter, push, t, toastError]);

  const updatePayment = useCallback(async (userId: string, patch: Record<string, unknown>) => {
    if (!adapter.updateStaffUser) throw new Error('update-unavailable');
    try {
      await adapter.updateStaffUser(userId, buildStaffProfilePatch(patch));
      push({ cat: 'users', sev: 'info', title: t.userPaymentSaved, body: '' });
      closeSheet();
    } catch (err) {
      toastError(err, 'userPayment');
      throw err;
    }
  }, [adapter, push, t, closeSheet, toastError]);

  const updateUser = useCallback(async (userId: string, patch: Record<string, unknown>) => {
    if (!adapter.updateStaffUser) throw new Error('update-unavailable');
    try {
      const profilePhoto = patch.profilePhoto instanceof File ? patch.profilePhoto : null;
      const ciFront = patch.ciFront instanceof File ? patch.ciFront : null;
      const ciBack = patch.ciBack instanceof File ? patch.ciBack : null;
      const rest = { ...patch };
      delete rest.profilePhoto;
      delete rest.ciFront;
      delete rest.ciBack;
      const mediaUrls: Record<string, string> = {};
      if (
        adapter.name === 'firebase'
        && (profilePhoto || ciFront || ciBack)
      ) {
        const houseId = String(patch.houseId || '').trim();
        if (houseId) {
          const { uploadStaffRegistrationMedia } = await import(
            '../services/firebase/staffMediaUpload'
          );
          Object.assign(mediaUrls, await uploadStaffRegistrationMedia({
            houseId,
            uid: userId,
            profilePhoto,
            ciFront,
            ciBack,
          }));
        }
      }
      await adapter.updateStaffUser(userId, {
        ...buildStaffProfilePatch(rest),
        ...mediaUrls,
      });
      push({
        cat: 'users',
        sev: 'success',
        title: t.userEditSaved,
        body: String(patch.name ?? ''),
      });
      closeSheet();
    } catch (err) {
      toastError(err, 'userEdit');
      throw err;
    }
  }, [adapter, push, t, closeSheet, toastError]);

  const deleteUser = useCallback(async (target: Record<string, unknown>, houseId: string) => {
    if (!adapter.deleteStaffUser || !canDeleteUser(viewer, target, houseId || undefined)) return;
    try {
      await adapter.deleteStaffUser(String(target.id));
      push({ cat: 'users', sev: 'warn', title: t.userDeleted, body: String(target.name ?? '') });
      closeSheet();
    } catch (err) {
      toastError(err, 'userDelete');
      throw err;
    }
  }, [adapter, viewer, push, t, closeSheet, toastError]);

  const sendMessage = useCallback(async (
    target: Record<string, unknown>,
    payload: { subject: string; body: string },
  ) => {
    if (!messagingAdapter || !viewer) return;
    try {
      await sendDirectMessage(messagingAdapter, viewer, target, {
        subject: payload.subject,
        body: payload.body,
        inApp: true,
        email: false,
      });
      push({ cat: 'users', sev: 'success', title: t.inboxMessageSent, body: String(target.name ?? '') });
      closeSheet();
    } catch (err) {
      toastError(err, 'userMessage');
      throw err;
    }
  }, [messagingAdapter, viewer, sendDirectMessage, push, t, closeSheet, toastError]);

  const mintReferralCode = useCallback(async (userId: string) => {
    const { ensureStaffReferralCode } = await import('../services/firebase/FunctionsService');
    const result = await ensureStaffReferralCode({ userId }) as { referralCode?: string };
    const code = String(result?.referralCode ?? '').toUpperCase();
    if (!code) throw new Error('referral_mint_failed');
    setSheet((prev) => {
      if (!prev || prev.type !== 'edit' || String(prev.user?.id) !== userId) return prev;
      return { ...prev, user: { ...prev.user, referralCode: code } };
    });
    push({
      cat: 'users',
      sev: 'success',
      title: t.staffReferralMinted ?? 'Referral code ready',
      body: code,
    });
    return code;
  }, [push, t]);

  const backfillReferrals = useCallback(async () => {
    const { backfillReferralCodes } = await import('../services/firebase/FunctionsService');
    const result = await backfillReferralCodes({ limit: 100 }) as {
      minted?: number;
      skipped?: number;
      scanned?: number;
    };
    push({
      cat: 'users',
      sev: 'success',
      title: t.staffReferralBackfillOk ?? 'Referral backfill done',
      body: (t.staffReferralBackfillBody
        ?? 'Minted {minted} · already had code {skipped} · scanned {scanned}')
        .replace('{minted}', String(result.minted ?? 0))
        .replace('{skipped}', String(result.skipped ?? 0))
        .replace('{scanned}', String(result.scanned ?? 0)),
    });
    return result;
  }, [push, t]);

  return {
    sheet,
    setSheet,
    closeSheet,
    handleAction,
    registerUser,
    updatePayment,
    updateUser,
    deleteUser,
    sendMessage,
    mintReferralCode,
    backfillReferrals,
  };
}
